import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'

interface GenericDeleteDialogProps<T extends { id: number | string; name: string }> {
    open: boolean
    onOpenChange: (open: boolean) => void
    item: T | null
    onConfirm: () => void
    isLoading?: boolean
    // Translation keys
    titleKey?: string
    descriptionKey?: string
    warningKey?: string
    permanentActionKey?: string
    deleteButtonKey?: string
    deletingKey?: string
    // Additional fields to display
    additionalFields?: Array<{
        labelKey: string
        value: string | number | boolean | null | undefined
        formatValue?: (value: any) => string
    }>
    // Custom render function for item details
    renderItemDetails?: (item: T) => React.ReactNode
}

export function GenericDeleteDialog<T extends { id: number | string; name: string }>({
    open,
    onOpenChange,
    item,
    onConfirm,
    isLoading = false,
    titleKey = 'common.deleteTitle',
    descriptionKey = 'common.confirmDelete',
    warningKey = 'common.deleteWarning',
    permanentActionKey = 'common.permanentAction',
    deleteButtonKey = 'common.delete',
    deletingKey = 'common.deleting',
    additionalFields = [],
    renderItemDetails,
}: GenericDeleteDialogProps<T>) {
    const { t } = useTranslation()
    if (!item) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t(titleKey)}
                    </DialogTitle>
                    <DialogDescription>
                        {t(descriptionKey)} "{item.name}"? {t(warningKey)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* General Warning */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t(permanentActionKey)}
                        </AlertDescription>
                    </Alert>

                    {/* Item Details */}
                    {renderItemDetails && (
                        <div className="bg-muted p-3 rounded-lg">
                            {renderItemDetails(item)}
                        </div>
                    )}
                    {additionalFields.length > 0 && (
                        <div className="bg-muted p-3 rounded-lg">
                            <div className="text-sm space-y-1">
                                {additionalFields.map((field, index) => (
                                    <div key={index}>
                                        <strong>{t(field.labelKey)}:</strong>{' '}
                                        {field.formatValue
                                            ? field.formatValue(field.value)
                                            : field.value?.toString() || t('common.notAvailable')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t(deletingKey) : t(deleteButtonKey)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

